<template>
  <div class="supply-chain-preference">
    <div class="pref-header">
      <span class="pref-back" @click="$emit('back')">&#8592; 返回工作台</span>
      <span class="pref-title">用户决策偏好</span>
      <span></span>
    </div>

    <div class="pref-row">
      <!-- 用户画像 -->
      <div class="pref-profile">
        <div class="profile-avatar-row">
          <img v-if="profileAvatar" :src="profileAvatar" class="profile-avatar profile-avatar-img" alt="" />
          <div v-else class="profile-avatar">CSO</div>
          <div class="profile-name-wrap">
            <div class="profile-name">供应链数字人</div>
            <div class="profile-role profile-role-link" @click="goDigitalProfile">查看档案</div>
          </div>
        </div>
        <div class="profile-tags" v-if="styleTags.length">
          <span v-for="t in styleTags" :key="t" class="tag-style">{{ t }}</span>
        </div>
        <div class="profile-auto-learn">
          <div>
            <div class="auto-learn-title">自动学习</div>
            <div class="auto-learn-desc">根据行为自动调整偏好值</div>
          </div>
          <el-switch v-model="autoLearn" @change="onAutoLearnChange" />
        </div>
        <div class="profile-log">
          <div class="log-header"><span class="log-dot"></span>调整日志 <span class="log-date">{{ lastUpdated }}</span></div>
          <div class="log-list">
            <div v-for="(item, i) in adjustLog" :key="i" class="log-item">
              <span :class="item.from < item.to ? 'arrow-up' : 'arrow-down'">{{ item.from < item.to ? '&#9650;' : '&#9660;' }}</span>
              <div class="log-info">
                <span class="log-dim">{{ item.dim }}</span>
                <span class="log-change">{{ item.from }} &#8594; {{ item.to }}</span>
                <span :class="item.from < item.to ? 'log-val-up' : 'log-val-down'">{{ item.from < item.to ? '+' : '' }}{{ item.to - item.from }}</span>
                <div class="log-meta">{{ item.time }} &#183; {{ item.reason }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 雷达图 + 风格标签 -->
      <div class="pref-radar-card">
        <div class="radar-main">
          <div class="radar-title"><span class="radar-dot"></span>决策倾向雷达图</div>
          <div ref="radarChart" class="radar-chart"></div>
        </div>
      </div>
    </div>

    <!-- 偏好维度配置 -->
    <div class="pref-config-card">
      <div class="config-header">
        <div class="config-title-row">
          <span class="config-dot"></span>
          <span class="config-title">偏好维度配置</span>
          <span class="config-hint">拖动滑块调整各维度偏好值，实时影响雷达图和决策风格</span>
        </div>
        <div class="config-actions">
          <button class="btn-reset" @click="resetToDefaults"><i class="fa fa-undo"></i> 恢复默认</button>
          <button class="btn-save" @click="savePreferences"><i class="fa fa-check"></i> 保存配置</button>
        </div>
      </div>
      <div class="config-group">
        <div v-for="group in dimGroups" :key="group.name" class="config-column">
          <div class="column-title"><span class="column-dot"></span>{{ group.name }}</div>
          <div v-for="d in group.dims" :key="d.key" class="slider-row">
            <div class="slider-label-row">
              <span class="slider-label">{{ d.label }}<span class="slider-desc"> — {{ d.desc }}</span></span>
              <span class="slider-value">{{ d.value }}</span>
            </div>
            <input type="range" min="0" max="100" step="5" :value="d.value"
              :style="{ background: 'linear-gradient(90deg, #4a5ff3 ' + d.value + '%, #e4e8f0 ' + d.value + '%)' }"
              @input="onSliderChange(d.key, $event)" />
            <div class="slider-extremes"><span>{{ d.low }} (0)</span><span>{{ d.high }} (100)</span></div>
          </div>
          <template v-if="group.extras === 'risk'">
            <div class="strategy-label">风险规避策略</div>
            <div class="strategy-options">
              <div v-for="s in riskStrategies" :key="s.key" class="strategy-opt"
                :class="{ active: riskStrategy === s.key }" @click="onStrategyChange(s.key)">
                {{ s.label }}<span class="strategy-sub">{{ s.sub }}</span>
              </div>
            </div>
          </template>
          <template v-if="group.extras === 'dialogue'">
            <div class="dialogue-hint">影响AI助手的对话风格：回复详略决定分析的详细程度，推送频率控制主动推送节奏，内容形态决定图表与文字的比例。</div>
          </template>
        </div>
      </div>
    </div>

    <!-- 图表行 -->
    <div class="pref-row">
      <div class="pref-chart-card">
        <div class="chart-card-title"><span class="chart-dot"></span>维度得分对比</div>
        <div ref="barChart" class="chart-box"></div>
      </div>
      <div class="pref-chart-card">
        <div class="chart-card-title"><span class="chart-dot"></span>偏好变化趋势（近30天）</div>
        <div ref="lineChart" class="chart-box"></div>
      </div>
    </div>

    <!-- 词云 + 决策记录 -->
    <div class="pref-row">
      <div class="pref-chart-card">
        <div class="chart-card-title"><span class="chart-dot"></span>最近决策记录</div>
        <div class="decision-list">
          <div v-for="(item, i) in recentDecisions" :key="i" class="decision-item">
            <span class="decision-time">{{ item.time }}</span>
            <span class="decision-dot"></span>
            <span class="decision-action">{{ item.action }}</span>
            <span class="decision-type" :style="{ background: item.typeBg, color: item.typeColor }">{{ item.typeLabel }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition name="fade">
      <div v-if="toastMsg" class="pref-toast">{{ toastMsg }}</div>
    </transition>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getSupplyChainAvatar, SUPPLY_CHAIN_INTERNAL_NAME } from '../utils/digitalHumanAvatar'
import { getAgentIdByInternalName } from '@/store/modules/cosmoClaw'

const DEFAULT_DATA = {
  user: { lastUpdated: '2026-06-24 10:30', autoLearnEnabled: true },
  dimensions: [
    { key: 'post_strategic_view', group: '岗位要求', label: '战略视野', value: 30, desc: '关注宏观战略 vs 微观执行', low: '纯执行', high: '纯战略' },
    { key: 'post_operation_granularity', group: '岗位要求', label: '运营精细度', value: 90, desc: '需要数据颗粒度粗细', low: '粗粒度摘要', high: '逐条明细' },
    { key: 'risk_tolerance', group: '风险习惯', label: '风险容忍度', value: 50, desc: '接受不确定性的程度', low: '零容忍', high: '完全接受' },
    { key: 'risk_alert_sensitivity', group: '风险习惯', label: '预警敏感度', value: 80, desc: '希望被预警的阈值高低', low: '仅重大预警', high: '任何异常' },
    { key: 'efficiency_response_style', group: '对话习惯', label: '回复详略', value: 60, desc: 'AI回复是精简摘要还是完整详实的资料包', low: '精简摘要', high: '完整详实' },
    { key: 'efficiency_push_freq', group: '对话习惯', label: '推送频率', value: 55, desc: 'AI主动推送分析报告的频次', low: '按需生成', high: '每日推送' },
    { key: 'efficiency_content_form', group: '对话习惯', label: '内容形态', value: 50, desc: '偏好图表可视化还是文字分析', low: '图表为主', high: '文字为主' }
  ],
  roleAverage: [35, 85, 55, 75, 55, 50, 50],
  platformAverage: [50, 60, 50, 60, 50, 50, 50],
  riskStrategy: 'BALANCED',
  strategyPresets: {
    CONSERVATIVE: { risk_tolerance: 20, risk_alert_sensitivity: 90 },
    BALANCED: { risk_tolerance: 50, risk_alert_sensitivity: 80 },
    AGGRESSIVE: { risk_tolerance: 80, risk_alert_sensitivity: 50 }
  },
  trend30Days: [
    { date: '05-26', strategic: 30, operation: 90, tolerance: 50, alert: 80, response: 60, push: 55, content: 50 },
    { date: '06-03', strategic: 30, operation: 90, tolerance: 50, alert: 80, response: 60, push: 55, content: 50 },
    { date: '06-11', strategic: 30, operation: 90, tolerance: 50, alert: 80, response: 55, push: 52, content: 50 },
    { date: '06-19', strategic: 32, operation: 90, tolerance: 48, alert: 82, response: 58, push: 55, content: 50 },
    { date: '06-23', strategic: 30, operation: 90, tolerance: 50, alert: 80, response: 60, push: 55, content: 50 }
  ],
  recentDecisions: [
    { time: '06-24 09:15', action: '查看了MCU库存紧急补货方案', type: 'decision_view' },
    { time: '06-23 16:40', action: '采纳了铜箔长协锁价方案B', type: 'decision_adopt' },
    { time: '06-23 14:20', action: '调整库存预警阈值：安全库存从15天→10天', type: 'alert_adjust' },
    { time: '06-22 11:00', action: '生成本月供应链经营分析报告', type: 'report_request' },
    { time: '06-21 09:30', action: '查看了供应商交期逾期告警详情', type: 'report_expand' },
    { time: '06-20 15:00', action: '将华大MCU认证方案标记为重点关注', type: 'decision_mark' },
    { time: '06-19 10:00', action: '查看了铜箔价格趋势分析（含LME期铜走势）', type: 'report_view' }
  ],
  topicTags: [
    { label: '库存管理', weight: 28 }, { label: '采购成本', weight: 24 }, { label: '供应商管理', weight: 20 },
    { label: '交期风险', weight: 18 }, { label: '质量认证', weight: 14 }, { label: '呆滞处置', weight: 10 },
    { label: '需求预测', weight: 8 }, { label: '物流配送', weight: 6 }
  ],
  adjustLog: [
    { time: '06-24 08:30', dim: '预警敏感度', from: 80, to: 82, reason: '连续3次点击展开告警详情' },
    { time: '06-23 14:20', dim: '推送频率', from: 55, to: 52, reason: '手动调低库存预警阈值' },
    { time: '06-22 16:00', dim: '风险容忍度', from: 50, to: 48, reason: '市场波动加剧，自动调低风险容忍' },
    { time: '06-21 10:00', dim: '回复详略', from: 60, to: 58, reason: '近5次对话选择"精简摘要"' }
  ],
  defaults: { post_strategic_view: 30, post_operation_granularity: 90, risk_tolerance: 50, risk_alert_sensitivity: 80, efficiency_response_style: 60, efficiency_push_freq: 55, efficiency_content_form: 50 }
}

// 相对今天偏移 offset 天的日期，格式 yyyy-MM-dd
function formatDay(offset) {
  const now = new Date()
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset)
  const pad = (n) => String(n).padStart(2, '0')
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}

// 相对今天偏移 offset 天的日期，格式 MM-DD
function formatMonthDay(offset) {
  const now = new Date()
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset)
  const pad = (n) => String(n).padStart(2, '0')
  return pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}

// 按记录顺序生成日期：最新一条为今天-1，其后依次再减 1 天，格式 MM-DD HH:mm
function toRecordTime(timeStr, index) {
  const hm = String(timeStr || '').split(' ')[1] || ''
  return formatMonthDay(index + 1) + (hm ? ' ' + hm : '')
}

// 今天-1（昨天）的时刻，格式 yyyy-MM-dd HH:mm
function yesterdayDateTime() {
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return formatDay(1) + ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes())
}

const CHART_THEME = {
  textColor: '#86909c',
  splitLineColor: 'rgba(0,0,0,0.05)',
  axisLineColor: 'rgba(0,0,0,0.08)'
}

const DECISION_TYPE_CONFIG = {
  decision_view: { label: '查看', bg: 'rgba(74,95,243,.08)', color: '#4a5ff3' },
  decision_adopt: { label: '采纳', bg: 'rgba(10,166,106,.08)', color: '#0aa66a' },
  alert_adjust: { label: '调阈值', bg: 'rgba(217,130,19,.08)', color: '#d98213' },
  report_request: { label: '生成报告', bg: 'rgba(127,85,217,.08)', color: '#7f55d9' },
  report_expand: { label: '查看详情', bg: 'rgba(148,163,184,.10)', color: '#64748b' },
  decision_mark: { label: '标记', bg: 'rgba(45,127,249,.08)', color: '#2d7ff9' },
  report_view: { label: '查看', bg: 'rgba(148,163,184,.10)', color: '#64748b' }
}

export default {
  name: 'SupplyChainPreference',
  data() {
    const data = JSON.parse(JSON.stringify(DEFAULT_DATA))
    return {
      dims: data.dimensions,
      roleAverage: data.roleAverage,
      platformAverage: data.platformAverage,
      riskStrategy: data.riskStrategy,
      strategyPresets: data.strategyPresets,
      trend30Days: data.trend30Days,
      recentDecisions: data.recentDecisions.map((d, idx) => {
        const cfg = DECISION_TYPE_CONFIG[d.type] || {}
        return { ...d, time: toRecordTime(d.time, idx), typeBg: cfg.bg || 'rgba(148,163,184,.10)', typeColor: cfg.color || '#64748b', typeLabel: cfg.label || d.type }
      }),
      topicTags: data.topicTags,
      adjustLog: data.adjustLog.map((item, idx) => ({ ...item, time: toRecordTime(item.time, idx) })),
      autoLearn: data.user.autoLearnEnabled,
      lastUpdated: formatDay(1) + ' ' + (String(data.user.lastUpdated).split(' ')[1] || '10:30'),
      defaults: data.defaults,
      toastMsg: '',
      toastTimer: null,
      radarChart: null,
      barChart: null,
      lineChart: null,
      riskStrategies: [
        { key: 'CONSERVATIVE', label: '保守', sub: '低容忍 高敏感' },
        { key: 'BALANCED', label: '平衡', sub: '中等容忍 中等敏感' },
        { key: 'AGGRESSIVE', label: '激进', sub: '高容忍 低敏感' }
      ]
    }
  },
  computed: {
    profileAvatar() {
      return getSupplyChainAvatar(this.$store.state.user.agentList)
    },
    dimGroups() {
      const groups = []
      const seen = {}
      this.dims.forEach(d => {
        if (!seen[d.group]) {
          seen[d.group] = true
          const extras = d.group === '风险习惯' ? 'risk' : d.group === '对话习惯' ? 'dialogue' : null
          groups.push({ name: d.group, dims: [], extras })
        }
        const g = groups.find(g => g.name === d.group)
        if (g) g.dims.push(d)
      })
      return groups
    },
    dimValues() {
      return this.dims.map(d => d.value)
    },
    dimLabels() {
      return this.dims.map(d => d.label)
    },
    styleTags() {
      return this.computeStyleTags(this.dims)
    },
    topicTagsWithSize() {
      const max = Math.max(...this.topicTags.map(t => t.weight))
      return this.topicTags.map(t => ({
        ...t,
        fontSize: 12 + (t.weight / max) * 8,
        size: t.weight >= 24 ? 'xl' : t.weight >= 18 ? 'l' : t.weight >= 12 ? 'm' : 's',
        opacity: 0.6 + (t.weight / max) * 0.4
      }))
    }
  },
  mounted() {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.initCharts()
      })
    })
    window.addEventListener('resize', this.handleResize)
    this.chartObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.reinitCharts()
      })
    })
  },
  beforeDestroy() {
    if (this.chartObserver) this.chartObserver.disconnect()
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    computeStyleTags(dims) {
      const v = (key) => { const x = dims.find(d => d.key === key); return x ? x.value : 50 }
      const tags = []
      if (v('post_strategic_view') >= 70 && v('post_operation_granularity') <= 40) tags.push('战略型')
      if (v('risk_tolerance') <= 30 && v('risk_alert_sensitivity') >= 70) tags.push('稳健型')
      if (v('risk_tolerance') >= 70 && v('risk_alert_sensitivity') <= 30) tags.push('激进型')
      if (v('efficiency_response_style') >= 70 && v('post_operation_granularity') >= 70) tags.push('分析型')
      if (v('efficiency_push_freq') <= 30 && v('efficiency_response_style') <= 40) tags.push('效率型')
      if (v('post_operation_granularity') >= 70 && v('risk_alert_sensitivity') >= 70) tags.push('运营型')
      if (tags.length === 0) tags.push('均衡型')
      return tags
    },
    getDim(key) {
      return this.dims.find(d => d.key === key)
    },
    setDim(key, val) {
      const d = this.getDim(key)
      if (d) d.value = parseInt(val)
    },
    onSliderChange(key, e) {
      const val = parseInt(e.target.value)
      this.setDim(key, val)
      this.drawAllCharts()
    },
    onStrategyChange(strategy) {
      this.riskStrategy = strategy
      const preset = this.strategyPresets[strategy]
      if (preset) {
        this.setDim('risk_tolerance', preset.risk_tolerance)
        this.setDim('risk_alert_sensitivity', preset.risk_alert_sensitivity)
      }
      this.drawAllCharts()
    },
    onAutoLearnChange(val) {
      this.showToast(val ? '自动学习已开启' : '自动学习已关闭')
    },
    goDigitalProfile() {
      const agentId = getAgentIdByInternalName(
        SUPPLY_CHAIN_INTERNAL_NAME,
        this.$store.state.user.agentList
      )
      const nav = this.$router.push({
        name: 'SmartEngineDigitalProfile',
        query: { agentId: String(agentId || '') }
      })
      if (nav && typeof nav.catch === 'function') nav.catch(() => {})
    },
    savePreferences() {
      this.lastUpdated = yesterdayDateTime()
      this.showToast('偏好配置已保存')
    },
    resetToDefaults() {
      this.$confirm('将恢复为供应链数字人的默认偏好配置，当前修改将丢失。确认恢复？', '恢复默认配置', {
        confirmButtonText: '确认恢复',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        Object.keys(this.defaults).forEach(k => this.setDim(k, this.defaults[k]))
        this.riskStrategy = 'BALANCED'
        this.lastUpdated = yesterdayDateTime()
        this.drawAllCharts()
        this.showToast('已恢复为默认配置')
      }).catch(() => {})
    },
    showToast(msg) {
      this.toastMsg = msg
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toastMsg = '' }, 1800)
    },
    handleResize() {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => this.resizeCharts(), 250)
    },
    initCharts() {
      this.drawRadar()
      this.drawBar()
      this.drawLine()
      // 注册 ResizeObserver 监听容器尺寸变化
      if (this.chartObserver) {
        const refs = [this.$refs.radarChart, this.$refs.barChart, this.$refs.lineChart]
        refs.forEach(el => { if (el) this.chartObserver.observe(el) })
      }
    },
    drawAllCharts() {
      this.drawRadar()
      this.drawBar()
      this.drawLine()
    },
    reinitCharts() {
      [this.radarChart, this.barChart, this.lineChart].forEach(chart => {
        if (chart && !chart.isDisposed()) chart.dispose()
      })
      this.radarChart = null
      this.barChart = null
      this.lineChart = null
      this.drawRadar()
      this.drawBar()
      this.drawLine()
    },
    resizeCharts() {
      if (this.radarChart && this.$refs.radarChart && this.$refs.radarChart.clientWidth > 0) this.radarChart.resize()
      if (this.barChart && this.$refs.barChart && this.$refs.barChart.clientWidth > 0) this.barChart.resize()
      if (this.lineChart && this.$refs.lineChart && this.$refs.lineChart.clientWidth > 0) this.lineChart.resize()
    },
    disposeCharts() {
      if (this.chartObserver) this.chartObserver.disconnect()
      if (this.radarChart) { this.radarChart.dispose(); this.radarChart = null }
      if (this.barChart) { this.barChart.dispose(); this.barChart = null }
      if (this.lineChart) { this.lineChart.dispose(); this.lineChart = null }
    },
    drawRadar() {
      const el = this.$refs.radarChart
      if (!el || el.clientWidth === 0 || el.clientHeight === 0) return
      if (!this.radarChart) this.radarChart = echarts.init(el)
      const valMap = {}
      this.dims.forEach(d => { valMap[d.key] = d.value })
      const order = ['post_strategic_view', 'efficiency_content_form', 'efficiency_push_freq', 'efficiency_response_style', 'risk_alert_sensitivity', 'risk_tolerance', 'post_operation_granularity']
      const indicators = order.map(k => {
        const d = this.dims.find(x => x.key === k)
        return { name: d ? d.label : k, max: 100 }
      })
      const radarValues = order.map(k => valMap[k] ?? 50)
      const avgOrder = ['post_strategic_view', 'post_operation_granularity', 'risk_tolerance', 'risk_alert_sensitivity', 'efficiency_response_style', 'efficiency_push_freq', 'efficiency_content_form']
      const radarAvg = order.map(k => {
        const idx = avgOrder.indexOf(k)
        return idx >= 0 ? this.roleAverage[idx] : 50
      })
      this.radarChart.setOption({
        radar: {
          center: ['50%', '54%'],
          radius: '62%',
          startAngle: 90,
          indicator: indicators,
          axisName: { color: '#4e5969', fontSize: 11, padding: [3, 5] },
          splitArea: { areaStyle: { color: ['rgba(74,95,243,0.02)', 'rgba(74,95,243,0.02)'] }},
          splitLine: { lineStyle: { color: CHART_THEME.splitLineColor }},
          axisLine: { lineStyle: { color: CHART_THEME.axisLineColor }}
        },
        series: [{
          type: 'radar',
          data: [
            { value: radarValues, name: '当前配置', areaStyle: { color: 'rgba(74,95,243,0.15)' }, lineStyle: { color: '#4a5ff3', width: 2 }, itemStyle: { color: '#4a5ff3' },
              label: { show: true, formatter: '{c}', color: '#4a5ff3', fontSize: 10, fontWeight: 600, offset: [0, -6] }},
            { value: radarAvg, name: 'CSO角色均值', areaStyle: { color: 'rgba(16,185,129,0.08)' }, lineStyle: { color: '#10b981', width: 1.5, type: 'dashed' }, itemStyle: { color: '#10b981' }}
          ],
          symbol: 'circle',
          symbolSize: 6
        }],
        legend: { bottom: 0, data: ['当前配置', 'CSO角色均值'], textStyle: { fontSize: 11, color: CHART_THEME.textColor }, itemWidth: 12, itemHeight: 8 }
      }, true)
    },
    drawBar() {
      const el = this.$refs.barChart
      if (!el || el.clientWidth === 0 || el.clientHeight === 0) return
      if (!this.barChart) this.barChart = echarts.init(el)
      this.barChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: ['当前配置', 'CSO角色均值', '平台均值'], textStyle: { fontSize: 11, color: CHART_THEME.textColor }},
        grid: { left: 40, right: 16, top: 10, bottom: 60 },
        xAxis: { type: 'category', data: this.dimLabels, axisLabel: { rotate: 30, fontSize: 10, color: CHART_THEME.textColor, interval: 0 }, axisLine: { lineStyle: { color: CHART_THEME.axisLineColor }}},
        yAxis: { type: 'value', max: 100, axisLabel: { fontSize: 10, color: CHART_THEME.textColor, margin: 8 }, splitLine: { lineStyle: { color: CHART_THEME.splitLineColor }}},
        series: [
          { name: '当前配置', type: 'bar', data: this.dimValues, itemStyle: { color: '#4a5ff3', borderRadius: [4, 4, 0, 0] }, emphasis: { itemStyle: { color: '#5168ff' }}, barGap: '20%', barCategoryGap: '40%' },
          { name: 'CSO角色均值', type: 'bar', data: this.roleAverage, itemStyle: { color: '#10b981', borderRadius: [4, 4, 0, 0] }, emphasis: { itemStyle: { color: '#11cb8d' }}},
          { name: '平台均值', type: 'bar', data: this.platformAverage, itemStyle: { color: '#d0d6e2', borderRadius: [4, 4, 0, 0] }, emphasis: { itemStyle: { color: '#e4e8f0' }}}
        ]
      }, true)
    },
    drawLine() {
      const el = this.$refs.lineChart
      if (!el || el.clientWidth === 0 || el.clientHeight === 0) return
      if (!this.lineChart) this.lineChart = echarts.init(el)
      const seriesKeys = ['strategic', 'operation', 'tolerance', 'alert', 'response', 'push', 'content']
      const seriesNames = ['战略视野', '运营精细度', '风险容忍度', '预警敏感度', '回复详略', '推送频率', '内容形态']
      const colors = ['#4a5ff3', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#64748b']
      this.lineChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: seriesNames, textStyle: { fontSize: 10, color: CHART_THEME.textColor }, itemWidth: 12, itemHeight: 8, itemGap: 8 },
        grid: { left: 40, right: 16, top: 10, bottom: 52 },
        xAxis: { type: 'category', data: this.trend30Days.map(d => d.date), axisLabel: { fontSize: 10, color: CHART_THEME.textColor }, axisLine: { lineStyle: { color: CHART_THEME.axisLineColor }}},
        yAxis: { type: 'value', min: 0, max: 100, axisLabel: { fontSize: 10, color: CHART_THEME.textColor }, splitLine: { lineStyle: { color: CHART_THEME.splitLineColor }}},
        series: seriesKeys.map((k, i) => ({
          name: seriesNames[i], type: 'line', data: this.trend30Days.map(d => d[k]),
          lineStyle: { color: colors[i], width: 2 }, itemStyle: { color: colors[i] }, symbol: 'circle', symbolSize: 4
        }))
      }, true)
    }
  }
}
</script>

<style lang="scss" scoped>
.supply-chain-preference {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 0 14px 14px;
  --blue2: #4a5ff3;
  --green: #10b981;
  --red: #ef4444;
  --amber: #f59e0b;
  --purple: #8b5cf6;
  --muted: #86909c;
  --text: #1a1a1a;
  --text2: #595959;
  --border: rgba(0,0,0,.06);
  --surface: #fff;
  --surface2: #f7f9fc;
  --shadow: 0 1px 3px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.06);
  box-sizing: border-box;
}

.pref-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0 10px;
  .pref-back {
    font-size: 13px;
    color: var(--blue2);
    cursor: pointer;
    font-weight: 600;
  }
  .pref-title {
    font-size: 16px;
    font-weight: 800;
    color: var(--text);
  }
}

.pref-row {
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
}

.pref-profile {
  flex: 0 0 280px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  min-height: 0;
  .profile-avatar-row {
    padding: 16px 16px 12px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .profile-avatar {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, #4a5ff3, #2f80ff);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 800;
    flex-shrink: 0;
  }
  .profile-avatar-img {
    object-fit: cover;
  }
  .profile-name { font-size: 15px; font-weight: 800; color: var(--text); }
  .profile-role { font-size: 11px; color: var(--muted); }
  .profile-role-link {
    cursor: pointer;
    color: var(--blue2);
    font-weight: 600;
    transition: opacity .15s;
    &:hover { opacity: .8; text-decoration: underline; }
  }

  .profile-tags {
    padding: 0 16px 10px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .tag-style {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    background: rgba(74,95,243,0.10);
    color: var(--blue2);
  }

  .profile-auto-learn {
    margin: 0 16px;
    padding: 10px 0;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    .auto-learn-title { font-size: 12px; font-weight: 600; color: var(--text); }
    .auto-learn-desc { font-size: 10px; color: var(--muted); margin-top: 1px; }
  }

  .profile-log {
    border-top: 1px solid var(--border);
    padding: 10px 16px 12px;
    flex: 1;
    overflow: hidden;
    .log-header { font-size: 11px; font-weight: 600; color: var(--text); margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
    .log-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: var(--green); }
    .log-date { font-weight: 400; color: var(--muted); }
    .log-list { overflow-y: auto; }
    .log-item {
      padding: 5px 0;
      border-bottom: 1px solid rgba(0,0,0,.04);
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-size: 11px;
      line-height: 1.6;
    }
    .arrow-up { color: var(--green); flex-shrink: 0; margin-top: 1px; font-size: 9px; }
    .arrow-down { color: var(--red); flex-shrink: 0; margin-top: 1px; font-size: 9px; }
    .log-dim { font-weight: 600; color: var(--text); }
    .log-change { color: var(--muted); }
    .log-val-up { color: var(--green); font-weight: 600; margin-left: 4px; }
    .log-val-down { color: var(--red); font-weight: 600; margin-left: 4px; }
    .log-meta { font-size: 10px; color: #94a3b8; margin-top: 1px; }
  }
}

.pref-radar-card {
  flex: 1;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px;
  box-shadow: var(--shadow);
  display: flex;
  gap: 16px;
  .radar-main { flex: 1; min-width: 0; }
  .radar-title { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 8px; }
  .radar-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--blue2); margin-right: 8px; }
  .radar-chart { width: 100%; height: 340px; }
}

.pref-config-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: var(--shadow);
  margin-bottom: 14px;
  .config-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .config-title-row { display: flex; align-items: center; gap: 8px; }
  .config-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--blue2); }
  .config-title { font-size: 13px; font-weight: 700; color: var(--text); }
  .config-hint { font-size: 10px; color: var(--muted); }
  .config-actions { display: flex; align-items: center; gap: 8px; }
  .config-group { display: flex; gap: 12px; }
  .config-column {
    flex: 1;
    min-width: 0;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 16px;
    box-shadow: var(--shadow);
  }
  .column-title { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 12px; }
  .column-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--blue2); margin-right: 8px; }
}

.slider-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  .slider-label-row { display: flex; align-items: center; justify-content: space-between; }
  .slider-label { font-size: 13px; font-weight: 600; color: var(--text); }
  .slider-desc { font-size: 11px; font-weight: 400; color: var(--muted); }
  .slider-value { font-size: 14px; font-weight: 800; color: var(--blue2); min-width: 28px; text-align: right; }
  .slider-extremes { display: flex; justify-content: space-between; font-size: 10px; color: #94a3b8; }
  input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 6px;
    border-radius: 3px;
    outline: none;
    cursor: pointer;
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: #fff;
      border: 2px solid var(--blue2);
      box-shadow: 0 2px 6px rgba(74,95,243,0.2);
      cursor: pointer;
    }
  }
}

.btn-save {
  padding: 6px 14px;
  border: none;
  border-radius: 7px;
  background: var(--blue2);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}
.btn-reset {
  padding: 6px 14px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: var(--surface);
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all .15s;
  &:hover { border-color: var(--red); color: var(--red); }
}

.strategy-label { margin-top: 8px; font-size: 12px; font-weight: 600; color: var(--text); }
.strategy-options { display: flex; gap: 8px; margin-top: 6px; }
.strategy-opt {
  flex: 1;
  padding: 10px 8px;
  border: 2px solid var(--border);
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  background: var(--surface);
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  transition: all .15s;
  &:hover { border-color: #c0cdf6; }
  &.active { border-color: var(--blue2); background: rgba(74,95,243,0.05); color: var(--blue2); }
  .strategy-sub { display: block; font-size: 10px; font-weight: 400; color: #94a3b8; margin-top: 2px; }
}

.dialogue-hint {
  margin-top: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(74,95,243,0.04);
  font-size: 11px;
  color: var(--muted);
  line-height: 1.5;
}

.pref-chart-card {
  flex: 1;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px;
  box-shadow: var(--shadow);
  .chart-card-title { font-size: 12px; font-weight: 700; color: var(--muted); margin-bottom: 8px; }
  .chart-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: var(--blue2); margin-right: 6px; }
  .chart-box { width: 100%; height: 260px; }
}

.word-cloud {
  width: 100%;
  height: 200px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  .word-item {
    padding: 5px 12px;
    border-radius: 999px;
    font-weight: 600;
    cursor: default;
    transition: transform .15s;
    &:hover { transform: scale(1.08); }
    &.wc-s { background: rgba(74,95,243,0.08); color: #4a5ff3; }
    &.wc-m { background: rgba(74,95,243,0.12); color: #3f50d8; }
    &.wc-l { background: rgba(74,95,243,0.16); color: #2e3ec4; }
    &.wc-xl { background: rgba(74,95,243,0.20); color: #1d2daa; font-weight: 800; }
  }
}

.decision-list { display: flex; flex-direction: column; gap: 4px; }
.decision-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
  font-size: 12px;
  line-height: 1.5;
  &:last-child { border-bottom: none; }
  .decision-time { color: var(--muted); font-size: 11px; white-space: nowrap; flex-shrink: 0; width: 104px; }
  .decision-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--blue2); margin-top: 6px; flex-shrink: 0; }
  .decision-action { color: var(--text); flex: 1; min-width: 0; }
  .decision-type { font-size: 10px; padding: 1px 6px; border-radius: 999px; flex-shrink: 0; font-weight: 600; }
}

.pref-toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  border-radius: 10px;
  background: var(--text);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  z-index: 9999;
}

.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
